import { useQuery } from '@tanstack/react-query';
import { companyWeekRange } from '@/lib/timesheetTime';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import { scheduleKeys } from '@/hooks/schedule/queryKeys';
import { fetchMyShifts } from '@/services/schedule/shiftService';

/**
 * Hook to fetch the count of unique scheduled days for the current week.
 * 
 * OPTIMIZATION: Shares cache with useMyShifts via scheduleKeys.myShifts().
 * Uses React Query's `select` to derive day count from the shared shifts cache.
 * This prevents duplicate fetches when both dashboard and MySchedule are mounted.
 * 
 * Cache invalidation: All mutations use scheduleKeys.all, so both hooks update together.
 */
export const useScheduledDays = (profileId: string | undefined) => {
  // Use the company-timezone week window so this widget describes the same week
  // as useWeeklyHours (previously device-local, causing split weeks near boundaries).
  const timezone = useCompanyTimezone();
  const { startISO: weekStartISO, endISO: weekEndISO } = companyWeekRange(new Date(), timezone);

  return useQuery({
    // SHARED cache key with useMyShifts - prevents duplicate fetches
    queryKey: scheduleKeys.myShifts(profileId || '', weekStartISO, weekEndISO),
    queryFn: async () => {
      if (!profileId) throw new Error('Profile ID required');
      return fetchMyShifts(profileId, weekStartISO, weekEndISO);
    },
    // DERIVE day count from cached shifts - no extra network request
    select: (shifts) => {
      const uniqueDays = new Set<string>();
      for (const shift of shifts) {
        // Extract date portion from start_time (YYYY-MM-DD)
        const date = shift.start_time.split('T')[0];
        uniqueDays.add(date);
      }
      return {
        daysCount: uniqueDays.size,
        shifts,
      };
    },
    enabled: !!profileId,
    staleTime: 2 * 60 * 1000, // 2 minutes - dashboard freshness per memory/features/dashboard
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
};
